/**
 * Application-level library read/sync instrumentation for A13.
 *
 * Records IDs, counts, source, and timing — never tokens, credentials, or
 * playlist contents. Measurement must not issue extra Firestore reads.
 *
 * These are application-observed query/doc counts, not Firebase billed reads.
 * The client SDK does not expose authoritative billing telemetry.
 */

const events = [];

const SENSITIVE_KEY = /token|secret|password|authorization|credential|api[_-]?key|access_token|refresh/i;

function diagnosticsFlagEnabled() {
  return process.env.REACT_APP_LIBRARY_METRICS === '1';
}

export function libraryMetricsEnabled() {
  return process.env.NODE_ENV === 'development'
    || process.env.NODE_ENV === 'test'
    || diagnosticsFlagEnabled();
}

function shouldLogToConsole() {
  return process.env.NODE_ENV === 'development' || diagnosticsFlagEnabled();
}

function isPlainScalar(value) {
  return value === null
    || typeof value === 'string'
    || typeof value === 'number'
    || typeof value === 'boolean';
}

function sanitizeMetric(event) {
  const safe = {};
  Object.entries(event).forEach(([key, value]) => {
    if (SENSITIVE_KEY.test(key)) return;
    if (!isPlainScalar(value)) return;
    safe[key] = value;
  });
  return safe;
}

export function recordLibraryMetric(event) {
  if (!libraryMetricsEnabled() || !event || typeof event !== 'object') {
    return null;
  }

  const recorded = {
    ...sanitizeMetric(event),
    billedReadsObservable: false,
    recordedAt: Date.now(),
  };
  events.push(recorded);
  if (shouldLogToConsole()) {
    console.info('[library-metrics]', recorded);
  }
  return recorded;
}

export function getLibraryMetrics() {
  return events.slice();
}

export function clearLibraryMetrics() {
  events.length = 0;
}

export function lastLibraryMetric(operation) {
  for (let index = events.length - 1; index >= 0; index -= 1) {
    if (!operation || events[index].operation === operation) {
      return events[index];
    }
  }
  return null;
}
